(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const config = window.NovaCart || {};
  const rootPath = (config.root || '/').endsWith('/') ? (config.root || '/') : `${config.root}/`;
  let toastTimer;

  const formatMoney = (cents, format = config.moneyFormat || 'R{{amount}}') => {
    const value = Number(cents || 0);
    const amount = (value / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const amountNoDecimals = Math.round(value / 100).toLocaleString();
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(/,/g, 'X').replace(/\./g, ',').replace(/X/g, '.'))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const showToast = (message) => {
    const toast = $('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-showing');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-showing'), 2600);
  };

  const setLocked = (locked) => document.body.classList.toggle('no-scroll', locked);

  const initReveal = () => {
    const items = $$('.reveal');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px' });
    items.forEach((item, index) => {
      item.style.setProperty('--reveal-delay', `${Math.min(index % 8, 7) * 45}ms`);
      observer.observe(item);
    });
  };

  const initHeader = () => {
    const header = $('[data-site-header]');
    if (!header) return;
    const main = $('.header-main', header);
    if (!main) return;
    const threshold = header.offsetTop + 140;
    const update = () => header.classList.toggle('is-stuck', window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initMobileMenu = () => {
    const menu = $('[data-mobile-menu]');
    if (!menu) return;
    const open = () => {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      setLocked(true);
    };
    const close = () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      setLocked(false);
    };
    $('[data-mobile-menu-open]')?.addEventListener('click', open);
    $$('[data-mobile-menu-close]', menu).forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  };

  const predictiveUrl = (query, limit = 6) => `${rootPath}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${limit}&resources[options][unavailable_products]=last`;

  const fetchProductSuggestions = async (query, limit = 6) => {
    const response = await fetch(predictiveUrl(query, limit), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Search failed');
    const data = await response.json();
    return data?.resources?.results?.products || [];
  };

  const initPredictiveSearch = () => {
    const wrapper = $('[data-predictive-search]');
    if (!wrapper) return;
    const input = $('input[type="search"]', wrapper);
    const results = $('[data-predictive-results]', wrapper);
    if (!input || !results) return;
    let timer;
    let requestId = 0;

    const close = () => {
      results.hidden = true;
      results.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
    };

    const render = (products, query) => {
      results.replaceChildren();
      const heading = document.createElement('div');
      heading.className = 'predictive-results__heading';
      heading.textContent = products.length ? 'Top matches' : 'No direct matches';
      results.appendChild(heading);

      products.forEach((product) => {
        const link = document.createElement('a');
        link.className = 'predictive-item';
        link.href = product.url;
        const image = document.createElement('img');
        image.loading = 'lazy';
        image.alt = product.title || '';
        image.src = product.featured_image?.url || product.image || '';
        const title = document.createElement('strong');
        title.textContent = product.title || 'Product';
        const price = document.createElement('span');
        price.textContent = formatMoney(product.price);
        link.append(image, title, price);
        results.appendChild(link);
      });

      const all = document.createElement('a');
      all.className = 'predictive-item';
      all.href = `${config.searchUrl || `${rootPath}search`}?q=${encodeURIComponent(query)}&type=product`;
      const blank = document.createElement('span');
      const label = document.createElement('strong');
      label.textContent = `View all results for “${query}”`;
      const arrow = document.createElement('span');
      arrow.textContent = '→';
      all.append(blank, label, arrow);
      results.appendChild(all);

      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) return close();
      timer = setTimeout(async () => {
        const id = ++requestId;
        try {
          const products = await fetchProductSuggestions(query);
          if (id === requestId) render(products, query);
        } catch (_) {
          close();
        }
      }, 220);
    });
    document.addEventListener('click', (event) => { if (!wrapper.contains(event.target)) close(); });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  };

  const updateCartBadges = (count) => {
    $$('[data-header-cart-count]').forEach((node) => { node.textContent = String(count); });
    $$('[data-cart-count]').forEach((node) => { node.textContent = `(${count})`; });
  };

  const renderCart = (cart) => {
    const container = $('[data-cart-items]');
    if (!container) return;
    container.replaceChildren();
    updateCartBadges(cart.item_count || 0);
    const total = $('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);

    if (!cart.items?.length) {
      const empty = createElement('div', 'empty-state');
      const icon = createElement('span', 'empty-state__icon', '🛒');
      const title = createElement('h3', '', 'Your cart is empty');
      const copy = createElement('p', '', 'Add something fire and it’ll show up here.');
      empty.append(icon, title, copy);
      container.appendChild(empty);
      return;
    }

    cart.items.forEach((item) => {
      const row = createElement('div', 'drawer-item');
      const imageLink = createElement('a', 'drawer-item__image');
      imageLink.href = item.url;
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.product_title || '';
        image.loading = 'lazy';
        imageLink.appendChild(image);
      }
      const details = document.createElement('div');
      const title = createElement('a', 'drawer-item__title', item.product_title || item.title);
      title.href = item.url;
      const variant = createElement('p', '', item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');
      const price = createElement('strong', '', formatMoney(item.final_line_price));
      const qty = createElement('div', 'qty');
      const minus = createElement('button', '', '−');
      minus.type = 'button';
      minus.dataset.cartKey = item.key;
      minus.dataset.cartQuantity = String(Math.max(0, item.quantity - 1));
      const amount = createElement('span', '', String(item.quantity));
      const plus = createElement('button', '', '+');
      plus.type = 'button';
      plus.dataset.cartKey = item.key;
      plus.dataset.cartQuantity = String(item.quantity + 1);
      qty.append(minus, amount, plus);
      details.append(title, variant, price, qty);
      row.append(imageLink, details);
      container.appendChild(row);
    });
  };

  const getCart = async () => {
    const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  const initCartDrawer = () => {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    const open = async () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      setLocked(true);
      try { await refreshCart(); } catch (_) { showToast('Could not refresh the cart.'); }
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      setLocked(false);
    };
    $$('[data-cart-open]').forEach((button) => button.addEventListener('click', open));
    $$('[data-cart-close]', drawer).forEach((button) => button.addEventListener('click', close));
    drawer.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-key]');
      if (!button) return;
      button.disabled = true;
      try {
        const response = await fetch(`${rootPath}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: button.dataset.cartKey, quantity: Number(button.dataset.cartQuantity) })
        });
        if (!response.ok) throw new Error('Update failed');
        renderCart(await response.json());
      } catch (_) {
        showToast('Could not update that item.');
      } finally {
        button.disabled = false;
      }
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
    window.NovaCartOpenCart = open;
  };

  const addFormToCart = async (form, submitButton) => {
    const originalText = submitButton?.textContent;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding…';
    }
    try {
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Could not add item');
      }
      const cart = await getCart();
      updateCartBadges(cart.item_count);
      showToast('Added to cart ✨');
      if (window.NovaCartOpenCart) window.NovaCartOpenCart();
    } catch (error) {
      showToast(error.message || 'Could not add to cart.');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    }
  };

  const initAjaxAdd = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-quick-add], [data-product-form]');
      if (!form) return;
      event.preventDefault();
      addFormToCart(form, $('button[type="submit"]', form));
    });
  };

  const bindWheelToHorizontal = (rail) => {
    if (!rail || rail.dataset.wheelBound === 'true') return;
    rail.dataset.wheelBound = 'true';
    rail.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || rail.scrollWidth <= rail.clientWidth + 4) return;
      const direction = Math.sign(event.deltaY);
      const atStart = rail.scrollLeft <= 2;
      const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
      if ((direction < 0 && atStart) || (direction > 0 && atEnd)) return;
      event.preventDefault();
      rail.scrollBy({ left: event.deltaY * 1.15, behavior: 'auto' });
    }, { passive: false });
  };

  const initRails = () => {
    $$('[data-horizontal-rail]').forEach((rail) => {
      const section = rail.closest('section') || rail.parentElement;
      const prev = $('[data-rail-prev]', section);
      const next = $('[data-rail-next]', section);
      const step = () => Math.max(280, rail.clientWidth * 0.78);
      prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      rail.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') rail.scrollBy({ left: -step(), behavior: 'smooth' });
        if (event.key === 'ArrowRight') rail.scrollBy({ left: step(), behavior: 'smooth' });
      });
      bindWheelToHorizontal(rail);
    });
  };

  const initCountdown = () => {
    $$('[data-countdown]').forEach((countdown) => {
      const tick = () => {
        const now = new Date();
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        const seconds = Math.max(0, Math.floor((end - now) / 1000));
        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');
        $('[data-hours]', countdown).textContent = h;
        $('[data-minutes]', countdown).textContent = m;
        $('[data-seconds]', countdown).textContent = s;
      };
      tick();
      setInterval(tick, 1000);
    });
  };

  const initProduct = () => {
    $$('[data-product-root]').forEach((root) => {
      const json = $('[data-product-json]', root);
      const form = $('[data-product-form]', root);
      if (!json || !form) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }
      const optionSelects = $$('[data-option-index]', form);
      const variantInput = $('[data-variant-id]', form);
      const price = $('[data-current-price]', root);
      const compare = $('[data-compare-price]', root);
      const addButton = $('[data-add-to-cart]', form);
      const addLabel = $('[data-add-label]', form);
      const stockStatus = $('[data-stock-status]', root);
      const viewport = $('[data-gallery-viewport]', root);
      const slides = $$('[data-gallery-slide]', root);
      const thumbs = $$('[data-gallery-thumb]', root);
      const counter = $('[data-gallery-current]', root);
      let activeIndex = 0;

      const setActiveSlide = (index, shouldScroll = false) => {
        if (!slides.length) return;
        activeIndex = Math.max(0, Math.min(index, slides.length - 1));
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
        thumbs.forEach((thumb) => thumb.classList.toggle('is-active', Number(thumb.dataset.galleryIndex) === activeIndex));
        if (counter) counter.textContent = String(activeIndex + 1);
        if (shouldScroll && viewport) slides[activeIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        thumbs[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };

      const scrollToMedia = (mediaId) => {
        const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
        if (index >= 0) setActiveSlide(index, true);
      };

      const setVariant = () => {
        const selected = optionSelects.map((select) => select.value);
        const variant = variants.find((item) => item.options.every((option, index) => option === selected[index])) || variants[0];
        if (!variant) return;
        variantInput.value = variant.id;
        price.textContent = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.hidden = false;
          compare.textContent = formatMoney(variant.compare_at_price);
        } else {
          compare.hidden = true;
        }
        addButton.disabled = !variant.available;
        addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';
        if (stockStatus) {
          stockStatus.classList.toggle('status-pill--online', variant.available);
          stockStatus.classList.toggle('status-pill--offline', !variant.available);
          stockStatus.innerHTML = `<i></i>${variant.available ? 'In stock and ready' : 'Currently sold out'}`;
        }
        const mediaId = variant.featured_media?.id || variant.featured_image?.id;
        if (mediaId) scrollToMedia(mediaId);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      optionSelects.forEach((select) => select.addEventListener('change', setVariant));

      $('[data-qty-minus]', form)?.addEventListener('click', () => {
        const input = $('input[name="quantity"]', form);
        input.value = Math.max(1, Number(input.value || 1) - 1);
      });
      $('[data-qty-plus]', form)?.addEventListener('click', () => {
        const input = $('input[name="quantity"]', form);
        input.value = Math.max(1, Number(input.value || 1) + 1);
      });

      thumbs.forEach((thumb) => thumb.addEventListener('click', () => setActiveSlide(Number(thumb.dataset.galleryIndex || 0), true)));
      $('[data-gallery-prev]', root)?.addEventListener('click', () => setActiveSlide(activeIndex - 1 < 0 ? slides.length - 1 : activeIndex - 1, true));
      $('[data-gallery-next]', root)?.addEventListener('click', () => setActiveSlide(activeIndex + 1 >= slides.length ? 0 : activeIndex + 1, true));

      if (viewport && slides.length > 1) {
        bindWheelToHorizontal(viewport);
        viewport.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') setActiveSlide(activeIndex - 1 < 0 ? slides.length - 1 : activeIndex - 1, true);
          if (event.key === 'ArrowRight') setActiveSlide(activeIndex + 1 >= slides.length ? 0 : activeIndex + 1, true);
        });
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) setActiveSlide(Number(visible.target.dataset.galleryIndex || 0));
          }, { root: viewport, threshold: [0.55, 0.75, 0.95] });
          slides.forEach((slide) => observer.observe(slide));
        }
      }
      const initial = slides.findIndex((slide) => slide.classList.contains('is-active'));
      setActiveSlide(initial >= 0 ? initial : 0, initial > 0);
    });
  };

  const initAiAssistant = () => {
    const assistant = $('[data-ai-assistant]');
    if (!assistant) return;
    const panel = $('[data-ai-panel]', assistant);
    const messages = $('[data-ai-messages]', assistant);
    const form = $('[data-ai-form]', assistant);
    const input = $('[data-ai-input]', assistant);
    const endpoint = assistant.dataset.apiEndpoint?.trim();
    const botName = assistant.dataset.botName || 'Nova AI';
    const supportPhone = assistant.dataset.supportPhone || '069 400 4994';
    const supportEmail = assistant.dataset.supportEmail || config.contactEmail || 'modisemagopa1@gmail.com';
    const deliveryMessage = assistant.dataset.deliveryMessage || 'Delivery options and estimates are calculated at checkout.';
    const returnMessage = assistant.dataset.returnMessage || 'Returns follow the published refund policy.';
    const welcome = $('.ai-message--bot p', messages)?.textContent || `Hey, I’m ${botName}. What are you shopping for?`;
    let localCatalog = [];
    try { localCatalog = JSON.parse($('[data-ai-catalog]', assistant)?.textContent || '[]'); } catch (_) { localCatalog = []; }

    const open = () => {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      assistant.classList.add('is-open');
      setTimeout(() => input?.focus(), 120);
    };
    const close = () => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      assistant.classList.remove('is-open');
    };
    $('[data-ai-open]', assistant)?.addEventListener('click', () => panel.classList.contains('is-open') ? close() : open());
    $('[data-ai-close]', assistant)?.addEventListener('click', close);

    const addMessage = (text, role = 'bot', thinking = false) => {
      const row = createElement('div', `ai-message ai-message--${role}${thinking ? ' is-thinking' : ''}`);
      const bubble = createElement('p', '', text);
      row.appendChild(bubble);
      messages.appendChild(row);
      messages.scrollTop = messages.scrollHeight;
      return row;
    };

    const addActions = (actions = []) => {
      if (!actions.length) return;
      const wrap = createElement('div', 'ai-actions');
      actions.forEach(({ label, href }) => {
        const link = createElement('a', 'ai-action', label);
        link.href = href;
        wrap.appendChild(link);
      });
      messages.appendChild(wrap);
      messages.scrollTop = messages.scrollHeight;
    };

    const addProducts = (products) => {
      const wrap = createElement('div', 'ai-product-results');
      products.slice(0, 5).forEach((product) => {
        const link = createElement('a', 'ai-product-link');
        link.href = product.url;
        if (product.featured_image?.url || product.image) {
          const image = document.createElement('img');
          image.src = product.featured_image?.url || product.image;
          image.alt = product.title || '';
          image.loading = 'lazy';
          link.appendChild(image);
        }
        const copy = createElement('span');
        const title = createElement('strong', '', product.title || 'Product');
        const productPrice = createElement('small', '', formatMoney(product.price));
        copy.append(title, productPrice);
        link.appendChild(copy);
        wrap.appendChild(link);
      });
      messages.appendChild(wrap);
      messages.scrollTop = messages.scrollHeight;
    };

    const getProductContext = () => {
      const node = $('[data-product-context]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_) { return null; }
    };

    const extractBudget = (message) => {
      const match = message.match(/(?:under|below|less than|max(?:imum)?|budget(?:\s+of)?)[^0-9]{0,12}(?:r|zar)?\s?([\d,]+)/i)
        || message.match(/(?:r|zar)\s?([\d,]+)[^a-z0-9]{0,8}(?:or less|max|budget)/i);
      return match ? Number(match[1].replace(/,/g, '')) * 100 : null;
    };

    const extractSearch = (message) => message
      .replace(/(?:under|below|less than|max(?:imum)?|budget(?:\s+of)?)[^0-9]{0,12}(?:r|zar)?\s?[\d,]+/gi, ' ')
      .replace(/\b(show|find|search|look|looking|recommend|suggest|need|want|buy|me|for|some|a|an|please|products?|items?|best|good|cheap|affordable)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const findProducts = async (query, budget = null, limit = 8) => {
      const cleanQuery = String(query || '').trim().toLowerCase();
      const isGeneric = !cleanQuery || /^(popular|anything|products|items|deals?)$/.test(cleanQuery);
      const localMatches = localCatalog.filter((product) => {
        const haystack = `${product.title || ''} ${product.vendor || ''} ${product.type || ''}`.toLowerCase();
        const words = cleanQuery.split(/\s+/).filter((word) => word.length > 1);
        const matchesQuery = isGeneric || words.every((word) => haystack.includes(word));
        const matchesBudget = !budget || Number(product.price) <= budget;
        return matchesQuery && matchesBudget;
      });
      const remote = isGeneric ? [] : await fetchProductSuggestions(cleanQuery, limit).catch(() => []);
      const merged = [...remote, ...localMatches].filter((product, index, all) => {
        const key = product.url || product.title;
        return all.findIndex((item) => (item.url || item.title) === key) === index;
      });
      return (budget ? merged.filter((product) => Number(product.price) <= budget) : merged).slice(0, limit);
    };

    const showSearchResults = async (query, budget = null) => {
      const products = await findProducts(query, budget, 10);
      if (products.length) {
        const budgetText = budget ? ` under ${formatMoney(budget)}` : '';
        addMessage(`These are the strongest matches${budgetText} 👀`);
        addProducts(products);
        addActions([{ label: 'View all search results', href: `${config.searchUrl || `${rootPath}search`}?q=${encodeURIComponent(query || '')}&type=product` }]);
        return true;
      }
      return false;
    };

    const localReply = async (message) => {
      const lower = message.toLowerCase().trim();
      const product = getProductContext();

      if (/^(hi|hey|hello|yo|sup|howzit|sharp)\b/.test(lower)) return `Hey 👋 Tell me the product and your budget, like “wireless headphones under R800”.`;
      if (/clear|reset chat|start over/.test(lower)) {
        messages.innerHTML = '';
        addMessage(welcome);
        return null;
      }
      if (/what.*(in|inside).*cart|cart summary|my cart|cart items/.test(lower)) {
        const cart = await getCart().catch(() => null);
        if (!cart) return 'I couldn’t load your cart right now. Tap the cart icon to check it.';
        if (!cart.item_count) {
          addActions([{ label: 'Start shopping', href: config.collectionsPath || '/collections' }]);
          return 'Your cart is empty rn 🛒 Tell me what you’re looking for and I’ll find options.';
        }
        const names = cart.items.slice(0, 4).map((item) => `${item.quantity}× ${item.product_title}`).join(', ');
        addActions([{ label: 'Open cart', href: config.cartPath || '/cart' }]);
        return `You have ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}: ${names}. Current total: ${formatMoney(cart.total_price)}.`;
      }
      if (product && /(this|current) product|how much|what.*price|in stock|available|tell me about/.test(lower)) {
        const stock = product.available ? 'It’s available to add to cart.' : 'It’s currently sold out.';
        return `${product.title}${product.vendor ? ` by ${product.vendor}` : ''} is ${formatMoney(product.price)}. ${stock}${product.description ? ` ${product.description}` : ''}`;
      }
      if (/deliver|shipping|ship|arrive|courier/.test(lower)) {
        addActions([{ label: 'Shipping policy', href: '/policies/shipping-policy' }]);
        return deliveryMessage;
      }
      if (/return|refund|exchange/.test(lower)) {
        addActions([{ label: 'Refund policy', href: '/policies/refund-policy' }]);
        return returnMessage;
      }
      if (/track|where.*order|order status/.test(lower)) return 'Use the tracking link in your dispatch email. If it hasn’t updated, contact support with your order number—but don’t share passwords, PINs or card details here.';
      if (/payment|pay|card|secure|eft|checkout/.test(lower)) return 'Available payment methods appear securely at Shopify checkout. NovaCart will never ask for your card PIN, banking password or one-time PIN in this chat.';
      if (/contact|human|support|phone|email|whatsapp/.test(lower)) {
        addActions([{ label: 'Email support', href: `mailto:${supportEmail}` }, { label: 'Call support', href: `tel:${supportPhone.replace(/\s/g, '')}` }]);
        return `Support: ${supportPhone} or ${supportEmail}. Include your order number when asking about an order.`;
      }
      if (/categor|browse|what.*sell|departments?/.test(lower)) {
        addActions([{ label: 'Browse all collections', href: config.collectionsPath || '/collections' }, { label: 'Shop all products', href: '/collections/all' }]);
        return 'You can browse Electronics, Fashion, Home, Gaming, Beauty, Sports and every collection added by the store owner.';
      }
      if (/how.*(order|buy)|place.*order/.test(lower)) return 'Choose a product, select its options, tap Add to cart, open your cart and continue to secure checkout. Delivery cost and timing are confirmed there.';
      if (/deal|sale|discount|special/.test(lower)) {
        const found = await showSearchResults('sale');
        if (found) return null;
        addActions([{ label: 'Shop all deals', href: '/collections/all' }]);
        return 'I couldn’t pull deal matches from search, but the Flash Deals rail and sale badges show active store offers.';
      }
      if (/compare\s+.+\s+(?:and|vs\.?|versus)\s+.+/i.test(message)) {
        const parts = message.split(/\s+(?:and|vs\.?|versus)\s+/i);
        const first = extractSearch(parts[0].replace(/compare/i, ''));
        const second = extractSearch(parts[1] || '');
        const results = [];
        if (first) results.push(...await findProducts(first, null, 1));
        if (second) results.push(...await findProducts(second, null, 1));
        if (results.length) {
          addMessage('Here are the closest products to compare:');
          addProducts(results);
          return 'Open each product to compare variants, full specs, delivery and availability.';
        }
      }

      const budget = extractBudget(message);
      let query = extractSearch(message);
      if (!query && budget) query = 'popular';
      if (query.length >= 2 || budget) {
        const found = await showSearchResults(query, budget);
        if (found) return null;
        return `I couldn’t find an exact match${query ? ` for “${query}”` : ''}${budget ? ` under ${formatMoney(budget)}` : ''}. Try a shorter product name or browse all products.`;
      }
      addActions([{ label: 'Browse products', href: '/collections/all' }, { label: 'Open cart', href: config.cartPath || '/cart' }]);
      return `I can search by product and budget, explain the current product, summarize your cart, and help with delivery, returns or support. Try “gaming mouse under R600”.`;
    };

    const ask = async (message) => {
      addMessage(message, 'user');
      const thinking = addMessage(`${botName} is thinking`, 'bot', true);
      try {
        let reply = null;
        if (endpoint && endpoint.startsWith('/') && !endpoint.startsWith('//')) {
          const cart = await getCart().catch(() => null);
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ message, context: { page: document.title, url: window.location.pathname, product: getProductContext(), cart } })
          });
          if (!response.ok) throw new Error('AI service unavailable');
          const data = await response.json();
          reply = data.reply || data.message;
          thinking.remove();
          if (reply) addMessage(String(reply));
          if (Array.isArray(data.products)) addProducts(data.products);
          if (Array.isArray(data.actions)) addActions(data.actions);
          return;
        }
        thinking.remove();
        reply = await localReply(message);
        if (reply) addMessage(reply);
      } catch (_) {
        thinking.remove();
        addMessage('I hit a tiny glitch 😭 Try the main search bar or contact NovaCart support.');
      }
    };

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const message = input.value.trim();
      if (!message) return;
      input.value = '';
      ask(message);
    });
    $$('[data-ai-prompt]', assistant).forEach((button) => button.addEventListener('click', () => {
      open();
      ask(button.dataset.aiPrompt);
    }));
    $('[data-ai-clear]', assistant)?.addEventListener('click', () => {
      messages.innerHTML = '';
      addMessage(welcome);
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  };

  const initFutureMotion = () => {
    const progress = $('[data-scroll-progress]');
    const updateProgress = () => {
      if (!progress) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progress.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    if (reduceMotion || !finePointer || config.animations === false) return;

    const aura = $('[data-pointer-aura]');
    const dot = $('[data-pointer-dot]');
    const ambient = $('[data-ambient]');
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let currentX = targetX;
    let currentY = targetY;

    if (config.cursorEffects !== false) document.body.classList.add('has-future-pointer');
    document.addEventListener('pointermove', (event) => {
      targetX = event.clientX;
      targetY = event.clientY;
      document.documentElement.style.setProperty('--pointer-x', `${event.clientX}px`);
      document.documentElement.style.setProperty('--pointer-y', `${event.clientY}px`);
      if (dot) dot.style.transform = `translate3d(${event.clientX}px,${event.clientY}px,0)`;
      if (ambient) {
        const x = (event.clientX / window.innerWidth - .5) * 18;
        const y = (event.clientY / window.innerHeight - .5) * 18;
        ambient.style.setProperty('--ambient-x', `${x}px`);
        ambient.style.setProperty('--ambient-y', `${y}px`);
      }
    }, { passive: true });

    const animatePointer = () => {
      currentX += (targetX - currentX) * .16;
      currentY += (targetY - currentY) * .16;
      if (aura) aura.style.transform = `translate3d(${currentX}px,${currentY}px,0)`;
      requestAnimationFrame(animatePointer);
    };
    animatePointer();

    if (config.cardTilt !== false) {
      $$('[data-tilt], .promo-card, .category-pill').forEach((card) => {
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;
          card.style.setProperty('--tilt-x', `${(x - .5) * 7}deg`);
          card.style.setProperty('--tilt-y', `${(.5 - y) * 7}deg`);
          card.style.setProperty('--spot-x', `${x * 100}%`);
          card.style.setProperty('--spot-y', `${y * 100}%`);
        });
        card.addEventListener('pointerleave', () => {
          card.style.setProperty('--tilt-x', '0deg');
          card.style.setProperty('--tilt-y', '0deg');
        });
      });
    }

    $$('.hero-card').forEach((hero) => hero.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      hero.style.setProperty('--hero-x', `${((event.clientX - rect.left) / rect.width - .5) * 20}px`);
      hero.style.setProperty('--hero-y', `${((event.clientY - rect.top) / rect.height - .5) * 14}px`);
    }));
  };

  document.addEventListener('DOMContentLoaded', () => {
    initReveal();
    initHeader();
    initMobileMenu();
    initPredictiveSearch();
    initCartDrawer();
    initAjaxAdd();
    initRails();
    initCountdown();
    initProduct();
    initAiAssistant();
    initFutureMotion();
  });
})();
